const FORBIDDEN_CHARS = /[;`{}[\]\\]/
const ALLOWED_EXPR = /^[\w\s().'"=!<>&|:+\-*/%,?]+$/
const RESERVED = new Set(['true', 'false', 'null', 'undefined'])

function asLiteral(value: unknown): string {
  if (value === undefined) return 'undefined'
  return JSON.stringify(value)
}

/**
 * Evaluate simple runtime condition expression from Designer node:
 * - supports Object.<field>
 * - supports boolean operators/comparisons: &&, ||, !, ==, ===, !=, !==, >, <, >=, <=
 * - returns true on empty/invalid expression to avoid accidental hiding due to syntax typos
 */
export function evaluateVisibleCondition(
  condition: string | undefined,
  values?: Record<string, unknown>
): boolean {
  if (!condition?.trim()) return true
  const raw = condition.trim()
  if (FORBIDDEN_CHARS.test(raw) || !ALLOWED_EXPR.test(raw)) return true

  const formValues = values ?? {}
  let expr = raw.replace(/\bObject\.([A-Za-z_]\w*)\b/g, (_m, key: string) => {
    return asLiteral(formValues[key])
  })

  expr = expr.replace(/\b([A-Za-z_]\w*)\b/g, (token: string) => {
    if (RESERVED.has(token)) return token
    if (Object.prototype.hasOwnProperty.call(formValues, token)) {
      return asLiteral(formValues[token])
    }
    return token
  })

  try {
    // eslint-disable-next-line no-new-func
    const out = Function(`"use strict"; return (${expr});`)()
    return Boolean(out)
  } catch {
    return true
  }
}

/**
 * enabledCondition uses the same expression semantics as visibleCondition.
 * Empty/invalid expression => enabled (fail-open).
 */
export function evaluateEnabledCondition(
  condition: string | undefined,
  values?: Record<string, unknown>
): boolean {
  return evaluateVisibleCondition(condition, values)
}

/** Shared contract: a Designer flag + a string condition (like FormNode / metadata). */
export interface RuntimeFieldVisibilityInput {
  /** false → hide (analog of FormNode.visible). Default: visible. */
  visible?: boolean
  visibleCondition?: string
}

export interface RuntimeFieldEnabledInput {
  /** false → disable (analog of FormNode.enabled). Default: enabled. */
  enabled?: boolean
  enabledCondition?: string
}

/** Node/field visibility: a static flag + visibleCondition. */
export function resolveRuntimeFieldVisible(
  input: RuntimeFieldVisibilityInput,
  values?: Record<string, unknown>
): boolean {
  if (input.visible === false) return false
  return evaluateVisibleCondition(input.visibleCondition, values)
}

/** Node/field availability: a static flag + enabledCondition. */
export function resolveRuntimeFieldEnabled(
  input: RuntimeFieldEnabledInput,
  values?: Record<string, unknown>
): boolean {
  if (input.enabled === false) return false
  return evaluateEnabledCondition(input.enabledCondition, values)
}
